import { useRef, useState } from "react";
import { ArrowUp, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { photoSrc } from "@/lib/designer";

export const PhotoTray = ({ cfg, photos, onUploaded, onPick, usedIds, label = "Sube tus fotos", hint, inputRef, onOpen }) => {
  const own = useRef(null);
  const input = inputRef || own;
  const [jobs, setJobs] = useState([]);

  const uploadOne = async (file, key) => {
    const set = (patch) => setJobs((js) => js.map((j) => (j.key === key ? { ...j, ...patch } : j)));
    if (file.size > cfg.max_mb * 1024 * 1024) return set({ error: `Supera ${cfg.max_mb} MB` });
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/designer/photos", fd, { onUploadProgress: (e) => set({ progress: e.total ? e.loaded / e.total : 0 }) });
      onUploaded(data);
      setJobs((js) => js.filter((j) => j.key !== key));
    } catch (e) { set({ error: errMsg(e) }); }
  };

  const onFiles = (files) => {
    const list = [...files].map((f, i) => ({ key: `${Date.now()}-${i}`, name: f.name, progress: 0, file: f }));
    setJobs((js) => [...js, ...list]);
    list.forEach((j) => uploadOne(j.file, j.key));
    input.current.value = "";
  };

  return (
    <div data-testid="photo-tray">
      <input ref={input} type="file" multiple hidden data-testid="photo-upload-input"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" onChange={(e) => onFiles(e.target.files)} />
      <button type="button" data-testid="photo-upload-button" onClick={() => { onOpen?.(); input.current.click(); }}
        className="flex w-full items-center justify-center gap-3 rounded-full bg-[#080808] py-5 text-sm font-semibold uppercase tracking-[0.16em] text-[#FAF8F4] shadow-[0_10px_24px_-12px_rgba(8,8,8,0.6)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#B18A4B]">
        {label} <ArrowUp className="h-5 w-5" />
      </button>
      {hint && <p data-testid="photo-upload-hint" className="mt-2 text-center text-sm text-[#080808]/70">{hint}</p>}
      <p className="mt-1 text-center text-xs text-[#080808]/50">JPEG, PNG, HEIC o WEBP · máximo {cfg.max_mb} MB por foto · puedes subir varias a la vez.</p>
      {jobs.length > 0 && (
        <ul className="mt-3 space-y-2">
          {jobs.map((j) => (
            <li key={j.key} data-testid="upload-job" className="text-xs">
              <div className="flex justify-between gap-2"><span className="truncate">{j.name}</span>
                {j.error ? <span className="flex shrink-0 items-center gap-2 text-[#8B1E1E]">{j.error}
                  <button type="button" aria-label="Descartar" data-testid="upload-job-dismiss" onClick={() => setJobs((js) => js.filter((x) => x.key !== j.key))} className="text-[#080808]/60 hover:text-[#080808]">✕</button></span>
                  : <span className="shrink-0">{Math.round(j.progress * 100)}%</span>}</div>
              {!j.error && <div className="mt-1 h-1 bg-[#EAE7E2]"><div className="h-1 bg-[#B18A4B] transition-[width]" style={{ width: `${j.progress * 100}%` }} /></div>}
              {!j.error && j.progress >= 1 && <span className="flex items-center gap-1 text-[#080808]/60"><Loader2 className="h-3 w-3 animate-spin" /> Procesando…</span>}
            </li>
          ))}
        </ul>
      )}
      {photos.length > 0 && (
        <p className="mt-5 text-xs font-semibold uppercase tracking-[0.12em] text-[#080808]/70">Tu bandeja · toca una foto para ponerla en el espacio seleccionado</p>
      )}
      {photos.length > 0 && (
        <div data-testid="photo-tray-grid" className="mt-3 grid grid-cols-5 gap-2">
          {photos.map((p) => (
            <button key={p.id} type="button" data-testid={`tray-photo-${p.id}`} onClick={() => onPick(p.id)} title="Usar en la celda seleccionada"
              className={`relative aspect-square overflow-hidden bg-[#EAE7E2] ring-offset-2 transition-shadow hover:ring-2 hover:ring-[#B18A4B] ${usedIds.has(p.id) ? "opacity-60" : ""}`}>
              <img src={photoSrc(p.id)} alt={p.original_filename || "Foto"} className="h-full w-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
